// DOM補助（tooltip / 検索クリアのバインド）
export const useDomEnhancements = ({
  selectedFolderId,
  hasSearchConditions,
  topOnly,
} = {}) => {
  const alignToolbarTooltip = (event) => {
    window.setTimeout(() => {
      const trigger = event.currentTarget;
      const tooltipId = trigger?.getAttribute('aria-describedby');
      if (!tooltipId) {
        return;
      }

      const tooltip = document.getElementById(tooltipId);
      if (!tooltip) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const tooltipHalfWidth = tooltipRect.width / 2;
      const horizontalBias = 0;
      const idealCenterX = triggerRect.left + triggerRect.width / 2 + horizontalBias;
      const minCenterX = tooltipHalfWidth + 4;
      const maxCenterX = viewportWidth - tooltipHalfWidth - 4;
      const appliedCenterX = Math.min(Math.max(idealCenterX, minCenterX), maxCenterX);
      const top = Math.round(triggerRect.bottom);

      tooltip.style.setProperty('position', 'fixed', 'important');
      tooltip.style.setProperty('left', `${appliedCenterX.toFixed(2)}px`, 'important');
      tooltip.style.setProperty('top', `${top}px`, 'important');
      tooltip.style.setProperty(
        'transform',
        'translate(-50%, 0)',
        'important'
      );
      tooltip.dataset.toolbarAligned = '1';

      const arrow = tooltip.querySelector('.arrow');
      if (!arrow) {
        return;
      }

      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      const arrowRect = arrow.getBoundingClientRect();
      const arrowCenterX = arrowRect.left + arrowRect.width / 2;
      const arrowCurrentLeft = Number.parseFloat(window.getComputedStyle(arrow).left) || 0;
      const centerOffset = triggerCenterX - arrowCenterX;
      arrow.style.setProperty(
        'left',
        `${(arrowCurrentLeft + centerOffset).toFixed(2)}px`,
        'important'
      );
    }, 40);
  };

  const initTooltips = () => {
    if (window.jQuery && typeof window.jQuery.fn.tooltip === 'function') {
      window.jQuery(() => {
        window.jQuery('[data-toggle="tooltip"]').tooltip({ container: 'body' });
        const toolbarTooltips = window.jQuery('.js-toolbar-tooltip');
        toolbarTooltips.off('inserted.bs.tooltip.toolbarAlign');
        toolbarTooltips.off('shown.bs.tooltip.toolbarAlign');
        toolbarTooltips.tooltip('dispose');
        toolbarTooltips.tooltip({
          container: 'body',
          boundary: 'window',
          placement: 'bottom',
          trigger: 'hover focus',
        });
        toolbarTooltips.on('inserted.bs.tooltip.toolbarAlign', alignToolbarTooltip);
        toolbarTooltips.on('shown.bs.tooltip.toolbarAlign', alignToolbarTooltip);
      });
    }
  };

  const bindClearSearchButton = () => {
    const clearButtons = Array.from(
      document.querySelectorAll(
        '#clear-search-btn, #mobile-clear-search-btn, [data-clear-dashboard-search="1"]'
      )
    );
    if (!clearButtons.length) {
      return () => { };
    }

    const handler = (event) => {
      event.preventDefault();
      const trigger = event.currentTarget;
      const form =
        trigger?.closest('form') || document.getElementById('simple-search-form');
      if (form) {
        const keyword = form.querySelector('input[name="q"]');
        if (keyword) {
          keyword.value = '';
        }
        const kindSelect = form.querySelector('select[name="kind"]');
        if (kindSelect) kindSelect.value = '';
        ['date_from', 'date_to'].forEach((name) => {
          const field = form.querySelector(`input[name="${name}"]`);
          if (field) field.value = '';
        });
        Array.from(form.querySelectorAll('input[name="tags[]"]')).forEach((node) =>
          node.remove()
        );
        Array.from(form.querySelectorAll('input[name="ai_tags[]"]')).forEach((node) =>
          node.remove()
        );
        Array.from(form.querySelectorAll('input[name="cad_tags[]"]')).forEach((node) =>
          node.remove()
        );
      }
      document.dispatchEvent(new CustomEvent('clear-dashboard-tags'));
      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      }
    };

    clearButtons.forEach((button) => button.addEventListener('click', handler));
    return () => {
      clearButtons.forEach((button) => button.removeEventListener('click', handler));
    };
  };

  // folder_id が送信可能な値か判定する（未選択/お気に入り仮想フォルダ等は非対象）
  const isSendableFolderId = (folderId) => {
    if (folderId === null || folderId === undefined || folderId === '') {
      return false;
    }
    const parsed = Number(folderId);
    return Number.isFinite(parsed) && parsed > 0;
  };

  // 検索フォーム(#mobile-search-form/#simple-search-form)のfolder_id hidden fieldを
  // 現在の選択フォルダ/検索スコープに同期する。全体検索中はdisabledにして送信自体から除外する
  // (has()はvalue不問でtrueになるため、値を空にするだけでは不十分)。
  // 対象は検索フォーム限定とし、動画作成/編集フォームやゴミ箱復元モーダルが持つ
  // 保存先選択の folder_id hidden input は巻き込まない。
  const syncFolderIdFields = (scope) => {
    const folderId = selectedFolderId?.value;
    const shouldSend = scope !== 'all' && isSendableFolderId(folderId);
    document
      .querySelectorAll(
        '#mobile-search-form input[name="folder_id"], #simple-search-form input[name="folder_id"]'
      )
      .forEach((field) => {
        field.disabled = !shouldSend;
        field.value = shouldSend ? String(folderId) : '';
      });
  };

  // 全フォームの hidden search_scope と「フォルダ内/全体」ボタンの見た目を scope に揃える
  const syncAllScope = (scope) => {
    document.querySelectorAll('input[name="search_scope"]').forEach((h) => {
      h.value = scope;
    });
    document.querySelectorAll('.js-search-scope-option').forEach((btn) => {
      const isActive = btn.dataset.scope === scope;
      btn.classList.toggle('btn-primary', isActive);
      btn.classList.toggle('btn-outline-secondary', !isActive);
    });
  };

  // フォームの検索 5 項目 (q / tags[] / ai_tags[] / cad_tags[] / kind / date_from / date_to) に指定があるか
  const formHasSearchConditions = (form) => {
    const q = form.querySelector('input[name="q"]');
    if (q && q.value.trim() !== '') return true;
    const hasValue = (selector) =>
      Array.from(form.querySelectorAll(selector)).some(
        (node) => !node.disabled && node.value !== ''
      );
    return (
      hasValue('input[name="tags[]"]') ||
      hasValue('input[name="ai_tags[]"]') ||
      hasValue('input[name="cad_tags[]"]') ||
      hasValue('select[name="kind"]') ||
      hasValue('input[name="date_from"]') ||
      hasValue('input[name="date_to"]')
    );
  };

  // 検索フォーム送信 (検索ボタン / Enter / クリアボタン) 時の検索範囲の調整。
  // 条件を入れても範囲は自動で切り替えない。フォルダ・お気に入りで「全体」にして検索した後に
  // 条件を全部消したときだけ、「フォルダ内」に戻す (TOP 表示中は「全体」のまま)。
  // 「フォルダ内/全体」ボタンは form.submit() で送るため submit イベントが発火せず対象外。
  const bindSearchScopeAutoSwitch = () => {
    const forms = ['#simple-search-form', '#mobile-search-form']
      .map((selector) => document.querySelector(selector))
      .filter(Boolean);
    if (!forms.length) {
      return () => {};
    }

    const handler = (event) => {
      const form = event.currentTarget;
      const hadConditions = !!hasSearchConditions?.value;
      const hasConditions = formHasSearchConditions(form);
      const scopeInput = form.querySelector('input[name="search_scope"]');
      const currentScope = scopeInput ? scopeInput.value : 'folder';

      if (hadConditions && !hasConditions && currentScope === 'all' && !topOnly?.value) {
        // folder_id は送らず、サーバが last_folder_id から直前のフォルダを復元する
        syncAllScope('folder');
      }
    };

    forms.forEach((form) => form.addEventListener('submit', handler));
    return () => {
      forms.forEach((form) => form.removeEventListener('submit', handler));
    };
  };

  const bindSearchScopeToggle = () => {
    const options = Array.from(
      document.querySelectorAll('.js-search-scope-option')
    );
    if (!options.length) {
      return () => {};
    }

    const syncAll = syncAllScope;

    const handler = (event) => {
      const button = event.currentTarget;
      const scope = button.dataset.scope;
      if (!scope) return;
      const form = button.closest('form');
      if (!form) return;
      const currentInput = form.querySelector('input[name="search_scope"]');
      if (currentInput && currentInput.value === scope) return;
      syncAll(scope);
      syncFolderIdFields(scope);
      form.submit();
    };

    options.forEach((btn) => btn.addEventListener('click', handler));
    return () => {
      options.forEach((btn) => btn.removeEventListener('click', handler));
    };
  };

  const init = () => {
    initTooltips();
    const unbindClear = bindClearSearchButton();
    const unbindScope = bindSearchScopeToggle();
    const unbindAutoSwitch = bindSearchScopeAutoSwitch();
    return () => {
      unbindClear();
      unbindScope();
      unbindAutoSwitch();
    };
  };

  return {
    initTooltips,
    bindClearSearchButton,
    bindSearchScopeToggle,
    bindSearchScopeAutoSwitch,
    syncFolderIdFields,
    init,
  };
};
